import React, { useRef, useEffect, useState } from 'react';
import {
	ScrollView,
	ScrollViewProps,
	KeyboardAvoidingView,
	Platform,
	Keyboard,
	type ScrollViewInstance,
} from 'react-native';

export interface KeyboardAwareScrollViewProps extends ScrollViewProps {
	/**
	 * Extra padding to add below the content when keyboard is visible
	 * Default: 80
	 */
	extraScrollHeight?: number;
	/**
	 * Whether to enable keyboard avoidance behavior
	 * Default: true on iOS/Android, false on web
	 */
	enableKeyboardAvoidance?: boolean;
	/**
	 * Offset from the top of the screen for KeyboardAvoidingView
	 * This accounts for headers/navigation bars that might cover content
	 * Default: 60 (to account for typical header height on iPad)
	 */
	keyboardVerticalOffset?: number;
	/**
	 * Whether to reset scroll position when keyboard hides
	 * Default: false
	 */
	resetScrollOnKeyboardHide?: boolean;
}

/**
 * A ScrollView that automatically adjusts for keyboard visibility.
 * On iOS and Android, wraps content in KeyboardAvoidingView and adds
 * extra padding when the keyboard is visible to ensure content can be scrolled.
 */
export const KeyboardAwareScrollView: React.FC<KeyboardAwareScrollViewProps> = ({
	children,
	extraScrollHeight = 80,
	enableKeyboardAvoidance,
	keyboardVerticalOffset = 60,
	resetScrollOnKeyboardHide = false,
	contentContainerStyle,
	style,
	...scrollViewProps
}) => {
	const scrollViewRef = useRef<ScrollViewInstance>(null);
	const [keyboardHeight, setKeyboardHeight] = useState(0);
	const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

	// Determine if we should enable keyboard avoidance
	// Default: enabled on native platforms (iOS/Android), disabled on web
	const isMobile = Platform.OS !== 'web';
	const shouldEnableKeyboardAvoidance = enableKeyboardAvoidance ?? isMobile;

	useEffect(() => {
		if (!shouldEnableKeyboardAvoidance) return;

		// Listen for keyboard show event
		const keyboardShowEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
		const keyboardHideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

		const keyboardShowListener = Keyboard.addListener(keyboardShowEvent, (event) => {
			setKeyboardHeight(event.endCoordinates.height);
			setIsKeyboardVisible(true);
		});

		const keyboardHideListener = Keyboard.addListener(keyboardHideEvent, () => {
			setKeyboardHeight(0);
			setIsKeyboardVisible(false);
			
			if (resetScrollOnKeyboardHide) {
				scrollViewRef.current?.scrollTo({ y: 0, animated: true });
			}
		});

		return () => {
			keyboardShowListener.remove();
			keyboardHideListener.remove();
		};
	}, [shouldEnableKeyboardAvoidance, resetScrollOnKeyboardHide]);

	// Calculate extra bottom padding when keyboard is visible
	// On iOS, KeyboardAvoidingView handles the padding, but we add extra scroll space
	// On Android, we need more padding since behavior="height" doesn't push content as well
	const keyboardPadding = isKeyboardVisible 
		? (Platform.OS === 'android' ? keyboardHeight + extraScrollHeight : extraScrollHeight)
		: extraScrollHeight;

	const scrollViewElement = (
		<ScrollView
			ref={scrollViewRef}
			style={style}
			contentContainerStyle={[
				{ flexGrow: 1 },
				contentContainerStyle,
				// Add extra padding at the bottom to ensure content can scroll above keyboard
				shouldEnableKeyboardAvoidance && { paddingBottom: keyboardPadding },
			]}
			keyboardShouldPersistTaps="handled"
			keyboardDismissMode="interactive"
			showsVerticalScrollIndicator
			scrollEventThrottle={16}
			automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
			{...scrollViewProps}
		>
			{children}
		</ScrollView>
	);

	// On native platforms, wrap with KeyboardAvoidingView
	if (shouldEnableKeyboardAvoidance) {
		return (
			<KeyboardAvoidingView
				style={{ flex: 1 }}
				behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
				keyboardVerticalOffset={keyboardVerticalOffset}
			>
				{scrollViewElement}
			</KeyboardAvoidingView>
		);
	}

	// On web or when disabled, just return the ScrollView
	return scrollViewElement;
};

export default KeyboardAwareScrollView;
